import PropTypes from "prop-types";
import { useState } from "react";
import { NavLink as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItemText,
  Typography,
  Divider,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { StyledNavItem, StyledNavItemIcon } from "./styles";

NavSection.propTypes = {
  data: PropTypes.array,
  collapse: PropTypes.bool,
  title: PropTypes.string,
};

export default function NavSection({
  data = [],
  collapse = false,
  title,
  ...other
}) {
  if (data.length === 0) {
    return null;
  }

  return (
    <Box {...other}>
      {title && !collapse && (
        <Typography
          variant="overline"
          sx={{
            pl: 3.5,
            pr: 2,
            py: 1,
            pb: 0,
            color: "text.secondary",
            display: "block",
            fontSize: "0.65rem",
            fontWeight: "fontWeightBold",
            textTransform: "uppercase",
            letterSpacing: 1.2,
            opacity: 0.72,
          }}
        >
          {title}
        </Typography>
      )}
      <List disablePadding sx={{ p: 1 }}>
        {data.map((item) => (
          <NavItem
            key={item.key ?? item.title}
            item={item}
            collapse={collapse}
          />
        ))}
      </List>
      <Divider
        sx={{
          mx: 2,
          my: 1,
          opacity: collapse ? 0 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}
      />
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.object,
  collapse: PropTypes.bool,
};

function NavItem({ item, collapse }) {
  const { title, path, icon, info, children } = item;
  const location = useLocation();
  const hasChildren = Array.isArray(children) && children.length > 0;
  const childActive =
    hasChildren &&
    children.some((child) => location.pathname === (child.path ?? path));
  const [open, setOpen] = useState(false);
  const expanded = open || childActive || location.pathname === path;

  return (
    <>
      <StyledNavItem
        component={RouterLink}
        to={path}
        sx={{
          justifyContent: collapse ? "center" : "flex-start",
          "&.active": {
            color: "text.primary",
            bgcolor: "action.selected",
            fontWeight: "fontWeightBold",
          },
        }}
      >
        <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>

        {!collapse && (
          <ListItemText
            disableTypography
            primary={title}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          />
        )}

        {!collapse && info && info}

        {!collapse && hasChildren && (
          <IconButton
            size="small"
            edge="end"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((prev) => !prev);
            }}
            sx={{ ml: 0.5, p: 0.25 }}
          >
            {expanded ? (
              <ExpandMore fontSize="small" />
            ) : (
              <ChevronRight fontSize="small" />
            )}
          </IconButton>
        )}
      </StyledNavItem>

      {hasChildren && !collapse && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {children.map((child) => (
              <SubNavItem
                key={child.key ?? child.title}
                item={child}
                parentPath={path}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

SubNavItem.propTypes = {
  item: PropTypes.object,
  parentPath: PropTypes.string,
};

function SubNavItem({ item, parentPath }) {
  const path = item.path ?? parentPath;
  return (
    <StyledNavItem
      component={RouterLink}
      to={path}
      end
      sx={{
        height: 34,
        pl: 5,
        "&.active": {
          color: "text.primary",
          fontWeight: "fontWeightBold",
        },
      }}
    >
      <ListItemText
        disableTypography
        primary={item.title}
        sx={{
          fontSize: 12,
          color: "text.secondary",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      />
    </StyledNavItem>
  );
}
